import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    AlertCircle,
    ArrowLeft,
    FlaskConical,
    PlayCircle,
    RotateCcw,
    Save,
    ScrollText,
    ShieldAlert,
    StopCircle,
    X,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EmptyState } from '@/components/data/EmptyState'
import { RoleGate } from '@/components/auth/RoleGate'
import { PolicyStatusBadge } from '@/components/tier-policy/PolicyStatusBadge'
import { TierEditor } from '@/components/tier-policy/TierEditor'
import { TierPolicyDiff } from '@/components/tier-policy/TierPolicyDiff'
import { DryRunResultDialog } from '@/components/tier-policy/DryRunResultDialog'
import { StageDialog } from '@/components/tier-policy/StageDialog'
import { RollbackDialog } from '@/components/tier-policy/RollbackDialog'
import { StageCountdown } from '@/components/tier-policy/StageCountdown'
import {
    useTierPolicy,
    useTierPolicyCancelStageMutation,
    useTierPolicyDraftMutation,
    useTierPolicyDryRunMutation,
    useTierPolicyRollbackMutation,
    useTierPolicyStageMutation,
    useTierPolicyVersions,
} from '@/hooks/api/useTierPolicy'
import { useActivePolicyTiers } from '@/hooks/chain/useActivePolicyTiers'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import { useRoles } from '@/hooks/useRoles'
import { ApiError } from '@/lib/api'
import { formatTimestamp } from '@/lib/utils'
import {
    POLICY_TIER_IDS,
    derivePolicyStatus,
    emptyTierDefinition,
    validateAddPolicyTiers,
    type DryRunResult,
    type PolicyTierId,
    type TierDefinition,
    type TierPolicy,
} from '@/types/tier-policy'
import { toast } from 'sonner'
import type { GateType } from '@/types/player-detail'

export const Route = createFileRoute('/tier-policy/$id')({
    component: TierPolicyDetailPage,
})

function TierPolicyDetailPage() {
    const { id } = Route.useParams()
    const { hasAnyRole, hasRole } = useRoles()
    const policyQuery = useTierPolicy(id)
    const versionsQuery = useTierPolicyVersions()

    if (!hasAnyRole('admin', 'mlro', 'read_only')) {
        return (
            <EmptyState
                icon={ShieldAlert}
                title="No access"
                description="Your operator session does not include a role that can view tier policies."
            />
        )
    }

    if (policyQuery.isLoading) {
        return <Skeleton className="h-96 w-full" />
    }

    if (policyQuery.isError) {
        const err = policyQuery.error
        const notFound = err instanceof ApiError && err.status === 404
        return (
            <EmptyState
                icon={AlertCircle}
                title={notFound ? 'Policy not found' : 'Could not load policy'}
                description={
                    notFound
                        ? 'No such policy id — maybe it was rolled back or never created.'
                        : err instanceof Error
                          ? err.message
                          : undefined
                }
            />
        )
    }

    if (!policyQuery.data) return null

    return (
        <DetailView
            policy={policyQuery.data}
            activePolicyId={versionsQuery.data?.versions.find((v) => v.isActive)?.policyId}
            canAdmin={hasRole('admin')}
            canDryRun={hasAnyRole('admin', 'mlro')}
        />
    )
}

function DetailView({
    policy,
    activePolicyId,
    canAdmin,
    canDryRun,
}: {
    policy: TierPolicy
    activePolicyId?: string
    canAdmin: boolean
    canDryRun: boolean
}) {
    const navigate = useNavigate()
    const status = derivePolicyStatus(policy)
    const { eligibleChainIds } = useOperatorSession()
    // Tier policies are global metadata, but the per-tier required-gate +
    // profit-threshold ladder lives in `KYCRegistry.activePolicy()` per chain.
    // Pick the operator's first eligible chain as the canonical read source —
    // every chain in scope is expected to carry the same active policy.
    const chainId = eligibleChainIds[0]
    const onChainPolicy = useActivePolicyTiers(chainId ?? 0)
    const chainTiers = onChainPolicy.data?.tiers ?? null
    const chainPolicyDescription = onChainPolicy.data?.description ?? null
    const chainGatesByTier = useMemo<Record<PolicyTierId, GateType[]> | null>(() => {
        if (!chainTiers) return null
        const out: Partial<Record<PolicyTierId, GateType[]>> = {}
        chainTiers.forEach((t, idx) => {
            const tid = POLICY_TIER_IDS[idx]
            if (tid) out[tid] = t.requiredGateTypes
        })
        return out as Record<PolicyTierId, GateType[]>
    }, [chainTiers])
    const chainInflowCapsByTier = useMemo<Record<PolicyTierId, string> | null>(() => {
        if (!chainTiers) return null
        const out: Partial<Record<PolicyTierId, string>> = {}
        chainTiers.forEach((t, idx) => {
            const tid = POLICY_TIER_IDS[idx]
            if (tid) out[tid] = t.inflowCapEurMinor.toString()
        })
        return out as Record<PolicyTierId, string>
    }, [chainTiers])
    const chainOutflowCapsByTier = useMemo<Record<PolicyTierId, string> | null>(() => {
        if (!chainTiers) return null
        const out: Partial<Record<PolicyTierId, string>> = {}
        chainTiers.forEach((t, idx) => {
            const tid = POLICY_TIER_IDS[idx]
            if (tid) out[tid] = t.outflowCapEurMinor.toString()
        })
        return out as Record<PolicyTierId, string>
    }, [chainTiers])

    // A draft or superseded version can be edited into a *new* draft (start
    // from a copy, change what you need, save). For active or staged versions
    // the editor is locked.
    const editableSeed = status === 'draft'
    const [draftTiers, setDraftTiers] = useState<TierDefinition[]>(() =>
        normalizeTiers(policy.tiers),
    )
    const [draftDescription, setDraftDescription] = useState(policy.description)
    const [editingAsNewDraft, setEditingAsNewDraft] = useState(false)
    const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null)
    const [dryRunOpen, setDryRunOpen] = useState(false)
    const [stageOpen, setStageOpen] = useState(false)
    const [rollbackOpen, setRollbackOpen] = useState(false)

    const draftMutation = useTierPolicyDraftMutation()
    const dryRunMutation = useTierPolicyDryRunMutation()
    const stageMutation = useTierPolicyStageMutation()
    const cancelStageMutation = useTierPolicyCancelStageMutation()
    const rollbackMutation = useTierPolicyRollbackMutation()

    const activePolicyQuery = useTierPolicy(
        activePolicyId && activePolicyId !== policy.policyId ? activePolicyId : undefined,
    )
    // Diff baseline = active policy's caps from DB, with required_gates AND
    // both directional caps overridden by the on-chain active policy (the
    // chain is authoritative for the ladder fields).
    const activeTiers = useMemo(() => {
        if (!activePolicyQuery.data?.tiers) return null
        const normalized = normalizeTiers(activePolicyQuery.data.tiers)
        return normalized.map((t) => {
            const tid = t.tier_id as PolicyTierId
            const chainGates = chainGatesByTier?.[tid]
            const chainInflow = chainInflowCapsByTier?.[tid]
            const chainOutflow = chainOutflowCapsByTier?.[tid]
            return {
                ...t,
                required_gates: chainGates ?? t.required_gates,
                inflow_cap_eur_minor: chainInflow ?? t.inflow_cap_eur_minor,
                outflow_cap_eur_minor: chainOutflow ?? t.outflow_cap_eur_minor,
            }
        })
    }, [activePolicyQuery.data, chainGatesByTier, chainInflowCapsByTier, chainOutflowCapsByTier])
    const showDiff = status === 'draft' && !!activeTiers

    const isReadOnly = !(editableSeed || editingAsNewDraft)
    const canSaveDraft = editingAsNewDraft || editableSeed

    // For active/staged read-only views, the editor mirrors the chain ladder
    // (required_gates + both directional caps) rather than the policy's
    // stored JSON. For drafts and edit-as-new-draft the user's local state
    // is the source of truth.
    const displayTiers = useMemo(() => {
        if (!isReadOnly) return draftTiers
        if (status !== 'active' && status !== 'staged') return draftTiers
        if (!chainGatesByTier && !chainInflowCapsByTier && !chainOutflowCapsByTier) {
            return draftTiers
        }
        return draftTiers.map((t) => {
            const tid = t.tier_id as PolicyTierId
            return {
                ...t,
                required_gates: chainGatesByTier?.[tid] ?? t.required_gates,
                inflow_cap_eur_minor: chainInflowCapsByTier?.[tid] ?? t.inflow_cap_eur_minor,
                outflow_cap_eur_minor: chainOutflowCapsByTier?.[tid] ?? t.outflow_cap_eur_minor,
            }
        })
    }, [
        draftTiers,
        isReadOnly,
        chainGatesByTier,
        chainInflowCapsByTier,
        chainOutflowCapsByTier,
        status,
    ])

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Button asChild variant="ghost" size="sm" className="-ml-2">
                    <Link to="/tier-policy">
                        <ArrowLeft className="h-3.5 w-3.5" /> Versions
                    </Link>
                </Button>
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold">Policy v{policy.version}</h1>
                    <PolicyStatusBadge status={status} />
                    {status === 'staged' && policy.activatedAt && (
                        <StageCountdown activatedAt={policy.activatedAt} />
                    )}
                    <div className="ml-auto">
                        <Button asChild variant="outline" size="sm">
                            <Link
                                to="/audit-log"
                                search={{
                                    subjectType: 'policy',
                                    subjectId: policy.policyId,
                                }}
                            >
                                <ScrollText className="h-3.5 w-3.5" /> View audit history
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Metadata</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-3 md:grid-cols-2">
                        <Meta label="Policy ID">
                            <code className="text-xs">{policy.policyId}</code>
                        </Meta>
                        <Meta label="Created">{formatTimestamp(policy.createdAt)}</Meta>
                        <Meta label="Author">
                            <span className="font-mono text-xs">{policy.createdBy ?? '—'}</span>
                        </Meta>
                        <Meta label="Activated">{formatTimestamp(policy.activatedAt)}</Meta>
                        <Meta label="Superseded">{formatTimestamp(policy.supersededAt)}</Meta>
                        <Meta label="Description" wide>
                            {canSaveDraft ? (
                                <Input
                                    value={draftDescription}
                                    onChange={(e) => setDraftDescription(e.target.value)}
                                    placeholder="Describe what changed and why"
                                />
                            ) : (
                                <p className="text-sm">
                                    {policy.description || (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </p>
                            )}
                        </Meta>
                        {chainPolicyDescription !== null && (
                            <Meta label="On-chain (canonical)" wide>
                                <p className="text-sm">
                                    {chainPolicyDescription || (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </p>
                            </Meta>
                        )}
                    </div>
                </CardContent>
            </Card>

            <ActionBar
                status={status}
                canAdmin={canAdmin}
                canDryRun={canDryRun}
                editingAsNewDraft={editingAsNewDraft}
                onStartNewDraft={() => {
                    // Seed the draft from the on-chain active policy when
                    // available so edits start from the canonical truth, not
                    // a possibly-stale DB snapshot of required_gates /
                    // directional caps.
                    if (chainGatesByTier || chainInflowCapsByTier || chainOutflowCapsByTier) {
                        setDraftTiers((prev) =>
                            prev.map((t) => {
                                const tid = t.tier_id as PolicyTierId
                                return {
                                    ...t,
                                    required_gates: chainGatesByTier?.[tid] ?? t.required_gates,
                                    inflow_cap_eur_minor:
                                        chainInflowCapsByTier?.[tid] ?? t.inflow_cap_eur_minor,
                                    outflow_cap_eur_minor:
                                        chainOutflowCapsByTier?.[tid] ?? t.outflow_cap_eur_minor,
                                }
                            }),
                        )
                    }
                    setEditingAsNewDraft(true)
                }}
                onDiscardNewDraft={() => {
                    setEditingAsNewDraft(false)
                    setDraftTiers(normalizeTiers(policy.tiers))
                    setDraftDescription(policy.description)
                }}
                saveDraftPending={draftMutation.isPending}
                onSaveDraft={() => {
                    const validationError = validateAddPolicyTiers(draftTiers)
                    if (validationError) {
                        toast.error('Invalid policy ladder', {
                            description: validationError,
                        })
                        return
                    }
                    draftMutation.mutate(
                        {
                            description: draftDescription,
                            tiers: draftTiers,
                        },
                        {
                            onSuccess: (res) => {
                                setEditingAsNewDraft(false)
                                navigate({
                                    to: '/tier-policy/$id',
                                    params: { id: res.policyId },
                                })
                            },
                        },
                    )
                }}
                dryRunPending={dryRunMutation.isPending}
                onDryRun={() => {
                    dryRunMutation.mutate(policy.policyId, {
                        onSuccess: (res) => {
                            setDryRunResult(res)
                            setDryRunOpen(true)
                        },
                    })
                }}
                onStage={() => setStageOpen(true)}
                cancelStagePending={cancelStageMutation.isPending}
                onCancelStage={() => cancelStageMutation.mutate(policy.policyId)}
                onRollback={() => setRollbackOpen(true)}
            />

            <Tabs defaultValue={POLICY_TIER_IDS[0]} className="space-y-4">
                <TabsList>
                    {POLICY_TIER_IDS.map((tid) => (
                        <TabsTrigger key={tid} value={tid}>
                            Tier {tid.slice(1)}
                        </TabsTrigger>
                    ))}
                    {showDiff && <TabsTrigger value="__diff">Diff vs active</TabsTrigger>}
                </TabsList>

                {POLICY_TIER_IDS.map((tid, idx) => {
                    const tier = displayTiers[idx]
                    const isTopTier = idx === POLICY_TIER_IDS.length - 1
                    return (
                        <TabsContent key={tid} value={tid}>
                            <TierEditor
                                tier={tier}
                                onChange={(next) => {
                                    setDraftTiers((prev) =>
                                        prev.map((t, i) => (i === idx ? next : t)),
                                    )
                                }}
                                readOnly={isReadOnly}
                                isTopTier={isTopTier}
                                prevTier={idx > 0 ? displayTiers[idx - 1] : undefined}
                            />
                        </TabsContent>
                    )
                })}

                {showDiff && (
                    <TabsContent value="__diff">
                        <TierPolicyDiff current={activeTiers!} draft={draftTiers} />
                    </TabsContent>
                )}
            </Tabs>

            <DryRunResultDialog
                open={dryRunOpen}
                onOpenChange={setDryRunOpen}
                result={dryRunResult}
            />
            <StageDialog
                open={stageOpen}
                onOpenChange={setStageOpen}
                pending={stageMutation.isPending}
                onConfirm={(activatedAtIso) => {
                    stageMutation.mutate(
                        { policyId: policy.policyId, activatedAt: activatedAtIso },
                        {
                            onSuccess: () => setStageOpen(false),
                        },
                    )
                }}
            />
            <RollbackDialog
                open={rollbackOpen}
                onOpenChange={setRollbackOpen}
                pending={rollbackMutation.isPending}
                version={policy.version}
                onConfirm={() => {
                    rollbackMutation.mutate(policy.policyId, {
                        onSuccess: () => setRollbackOpen(false),
                    })
                }}
            />
        </div>
    )
}

function ActionBar({
    status,
    canAdmin,
    canDryRun,
    editingAsNewDraft,
    onStartNewDraft,
    onDiscardNewDraft,
    saveDraftPending,
    onSaveDraft,
    dryRunPending,
    onDryRun,
    onStage,
    cancelStagePending,
    onCancelStage,
    onRollback,
}: {
    status: ReturnType<typeof derivePolicyStatus>
    canAdmin: boolean
    canDryRun: boolean
    editingAsNewDraft: boolean
    onStartNewDraft: () => void
    onDiscardNewDraft: () => void
    saveDraftPending: boolean
    onSaveDraft: () => void
    dryRunPending: boolean
    onDryRun: () => void
    onStage: () => void
    cancelStagePending: boolean
    onCancelStage: () => void
    onRollback: () => void
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Actions</CardTitle>
                <CardDescription>Mutating actions are gated on operator role.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
                {(status === 'active' || status === 'staged' || status === 'superseded') &&
                    !editingAsNewDraft && (
                        <RoleGate roles={['admin']}>
                            <Button variant="outline" onClick={onStartNewDraft}>
                                <Save className="h-3.5 w-3.5" /> Edit as new draft
                            </Button>
                        </RoleGate>
                    )}
                {(status === 'draft' || editingAsNewDraft) && (
                    <RoleGate roles={['admin']}>
                        <Button onClick={onSaveDraft} disabled={saveDraftPending || !canAdmin}>
                            <Save className="h-3.5 w-3.5" />
                            {saveDraftPending ? 'Saving…' : 'Save as new draft'}
                        </Button>
                    </RoleGate>
                )}
                {editingAsNewDraft && (
                    <Button variant="ghost" onClick={onDiscardNewDraft}>
                        <X className="h-3.5 w-3.5" /> Discard changes
                    </Button>
                )}
                {status === 'draft' && (
                    <Button
                        variant="outline"
                        onClick={onDryRun}
                        disabled={dryRunPending || !canDryRun}
                    >
                        <FlaskConical className="h-3.5 w-3.5" />
                        {dryRunPending ? 'Running…' : 'Dry run'}
                    </Button>
                )}
                {status === 'draft' && (
                    <RoleGate roles={['admin']}>
                        <Button onClick={onStage}>
                            <PlayCircle className="h-3.5 w-3.5" /> Stage
                        </Button>
                    </RoleGate>
                )}
                {status === 'staged' && (
                    <RoleGate roles={['admin']}>
                        <Button
                            variant="destructive"
                            onClick={onCancelStage}
                            disabled={cancelStagePending}
                        >
                            <StopCircle className="h-3.5 w-3.5" />
                            {cancelStagePending ? 'Cancelling…' : 'Cancel stage'}
                        </Button>
                    </RoleGate>
                )}
                {status === 'superseded' && (
                    <RoleGate roles={['admin']}>
                        <Button variant="outline" onClick={onRollback}>
                            <RotateCcw className="h-3.5 w-3.5" /> Rollback
                        </Button>
                    </RoleGate>
                )}
            </CardContent>
        </Card>
    )
}

function Meta({
    label,
    children,
    wide,
}: {
    label: string
    children: React.ReactNode
    wide?: boolean
}) {
    return (
        <div className={wide ? 'md:col-span-2' : undefined}>
            <div className="text-muted-foreground text-[11px] tracking-wide uppercase">{label}</div>
            <div className="mt-1">{children}</div>
        </div>
    )
}

// The backend may send tiers out of order or with a missing entry. Normalize
// to the canonical T0..T4 order, filling any gaps with an empty definition.
// Merge each tier over the empty default so the editor never reads an
// undefined field.
function normalizeTiers(tiers: TierDefinition[]): TierDefinition[] {
    const byId = new Map(tiers.map((t) => [t.tier_id, t]))
    return POLICY_TIER_IDS.map((tid) => {
        const empty = emptyTierDefinition(tid)
        const got = byId.get(tid)
        if (!got) return empty
        return {
            ...empty,
            ...got,
            required_gates: got.required_gates ?? [],
        }
    })
}
